import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { Media } from '@klickdata/core/media/src/media.model';
import { MessageDeletedComponent, MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, ResourceTag, ResourceTypes } from '@klickdata/core/resource';
import { MaterialModelType } from '@klickdata/core/resource/src/resource.model';
import { SelectFilterOption, TableFilterComponent } from '@klickdata/core/table';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { MatTableBase } from 'apps/klickdata/src/app/shared/table/mat-table-base';
import { filter, switchMap, takeUntil, tap } from 'rxjs';
import { DialogVideoPlayerComponent } from '../../dialog-video-player/dialog-video-player.component';
import { K3MediaListingService } from '../k3-media-listing.service';
import { ListingContext } from '@klickdata/core/application/src/model/model-interface';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
    selector: 'app-audio-listing-general',
    templateUrl: './audio-listing-general.component.html',
    styleUrls: ['./audio-listing-general.component.scss'],
    providers: [K3MediaListingService],
})
export class AudioListingGeneralComponent extends MatTableBase<Media> implements AfterViewInit {
    @Output() onMediaClick: EventEmitter<Media> = new EventEmitter<Media>();
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    @Input() public columns = ['id', 'filename', 'description', 'tags', 'visibility', 'author', 'updated_at', 'tools'];
    @Input() public hasExpansionFilter = true;
    @Input() public listingContext: ListingContext;
    @Input() public pageSize = 25;
    onLoadingAIMedia: EventEmitter<boolean> = new EventEmitter<boolean>();
    AppScope = AppScope;
    public authUser: User;
    public filePrioOptions: SelectFilterOption[];

    constructor(
        protected listService: K3MediaListingService,
        protected mobileService: MobileService,
        protected cdr: ChangeDetectorRef,
        protected dialog: MatDialog,
        protected route: ActivatedRoute,
        protected router: Router,
        protected message: MessageService,
        protected auth: AuthService,
        protected clipboard: Clipboard
    ) {
        super(cdr, mobileService, listService);
        this.filePrioOptions = Utils.getTaskPrioOptions();
        this.auth
            .getUser()
            .pipe(takeUntil(this.destroy))
            .subscribe((user) => (this.authUser = user));
    }
    ngOnInit(): void {
        this.listService
            .listenToMediaEvent({ type: MaterialModelType.AUDIO })
            .pipe(
                takeUntil(this.destroy),
                filter((media) => media.listingContext === this.listingContext)
            )
            .subscribe((media) => {
                this.dataSource.replace(media);
                this.onLoadingAIMedia.emit(false);
            });
    }
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.route.queryParams.pipe(filter((params) => params.action === 'addAudio')).subscribe(() =>
            setTimeout(() => {
                this.addAudio();
                this.router.navigate([this.router.url.split('?')[0]]);
            }, 500)
        );
        this.onLoadingAIMedia.next(this.authUser?.isProcessingState() ?? false);
    }
    public onFilterByFavorite(filter: boolean) {
        if (filter) {
            this.filter.createOrUpdateFilter([{ property: 'favorite', items: [this.authUser.id] }]);
        } else {
            this.filter.ignoreFilter(['favorite']);
        }
    }
    public addAudio() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.AI_CREATE_UPDATE_MEDIA,
            data: {
                type: 'addAudio',
                aiModelType: MaterialModelType.AUDIO,
                title: $localize`Add a new audio`,
                icon: 'record_voice_over',
                uploadBtnText: $localize`Upload an audio`,
                aiBtnText: $localize`Create an audio with AI`,
                allowedMimeType: 'audio/mpeg, audio/mp3, audio/wav, audio/ogg, audio/webm',
                aiPromptPlaceholder: $localize`Write a prompt and let AI create an audio`,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                tap(() => this.router.navigate([], { queryParams: null })),
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'addAudio'),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data.mediaSource === 'upload') {
                    this.dataSource.replace(new Media(data.value));
                } else {
                    this.onLoadingAIMedia.emit(true);
                }
                this.cdr.markForCheck();
            });
    }
    public edit(media: Media) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.AI_CREATE_UPDATE_MEDIA,
            data: {
                type: 'editAudio',
                aiModelType: MaterialModelType.AUDIO,
                title: $localize`Edit audio`,
                icon: 'record_voice_over',
                uploadBtnText: $localize`Upload an audio`,
                aiBtnText: $localize`Create an audio with AI`,
                allowedMimeType: 'audio/mpeg, audio/mp3, audio/wav, audio/ogg, audio/webm',
                aiPromptPlaceholder: $localize`Write a prompt and let AI create an audio`,
                media: media,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                tap(() => this.router.navigate([], { queryParams: null })),
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'editAudio'),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data.mediaSource === 'upload') {
                    this.dataSource.replace(new Media(data.value));
                } else {
                    this.onLoadingAIMedia.emit(true);
                }
                this.cdr.markForCheck();
            });
    }
    public createMaterial(media: Media) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.CREATE_RESOURCE,
            data: {
                typeId: ResourceTypes.DocMaterial,
                items: [{ name: media.path, item_type_value: media.type }],
            },
        });
    }
    public favorite(media: Media) {
        media.favorite = !media.favorite;
        this.listService
            .updateMedia(+media.id, { favorite: media.favorite }, 'tags,user_feeds')
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
                this.refresh();
                this.message.openMessage(MessageSavedComponent, $localize`Favorite updated`);
            });
    }

    public delete(id: number) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete Audio`,
                contentBody: $localize`You are about to delete this audio. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap((res) => {
                    if (res) {
                        return this.listService.destroy(id);
                    }
                })
            )
            .subscribe((action) => {
                if (action) {
                    this.message.openMessage(MessageDeletedComponent);
                    this.refresh();
                }
            });
    }
    public view(media: Media) {
        this.dialog.open(DialogVideoPlayerComponent, {
            maxWidth: '60%',
            data: {
                id: media.id,
                autoplay: false,
            },
        });
    }
    onTagClick(tag: ResourceTag | any) {
        this.filter.filterByModel(new ResourceTag(tag).getFilter(), true);
    }
    public updateMediaTags(media: Media, tagsIds: number[]) {
        this.listService.updateMedia(+media.id, { tag_ids: tagsIds }, 'tags').pipe(takeUntil(this.destroy)).subscribe();
    }
    onSelectPriority(media: Media, prio: string) {
        this.listService
            .updateMedia(+media.id, { priority: prio })
            .pipe(
                takeUntil(this.destroy),
                tap(() => this.refresh())
            )
            .subscribe();
    }

    public copyLink(link: string) {
        this.clipboard.copy(link);
        this.message.openMessage(MessageSavedComponent, $localize`Link copied to clipboard`);
    }
}
